import type { ImportFieldNode } from '@erp/rpc';
import type {
  SchemaFieldIndexType,
  SqlSchemaFieldType,
} from '@erp/shared-types';
import {
  allowedIndexTypesForFieldType,
  SQL_SCHEMA_FIELD_TYPES,
} from '@erp/shared-types';
import {
  parseFlexibleDateOnly,
  sqlDefaultValueConstraintError,
  type SqlNumericArgs,
} from '@erp/shared-utils';
import {
  isPlainObject,
  parseJsonObjectInput,
  toDisplayLabel,
} from './schemaParserShared';

export type ParseResult =
  | { ok: true; fields: ImportFieldNode[]; keyedBy: string[] }
  | { ok: false; error: string };

const SQL_SCHEMA_FIELD_TYPE_SET = new Set<string>(SQL_SCHEMA_FIELD_TYPES);

/**
 * Thrown by `buildSqlSchemaConfig` / `stringifySqlSchemaConfig` when a field
 * uses a `SchemaFieldType` outside the restricted "sql" vocabulary
 * (string/integer/numeric/date — see `SQL_SCHEMA_FIELD_TYPES`). "sql" schemas
 * describe columns 1:1 mappable to Postgres primitives, not Extend AI
 * extraction targets, so this vocabulary is intentionally smaller than (and
 * independent of) `extendAISchemaParser`'s.
 */
export class UnsupportedSqlSchemaFieldTypeError extends Error {
  readonly fieldName: string;
  readonly fieldType: string;

  constructor(fieldName: string, fieldType: string) {
    super(
      `Field "${fieldName}" has type "${fieldType}", which a "sql" schema cannot express. ` +
        `Supported types are: ${SQL_SCHEMA_FIELD_TYPES.join(', ')}.`,
    );
    this.name = 'UnsupportedSqlSchemaFieldTypeError';
    this.fieldName = fieldName;
    this.fieldType = fieldType;
  }
}

function describe(propSchema: Record<string, unknown>): string | null {
  return typeof propSchema.description === 'string'
    ? propSchema.description
    : null;
}

/**
 * Thrown while parsing a single field; caught by `parseSqlSchema` and turned
 * into a `ParseResult` error naming the offending field.
 */
class FieldParseError extends Error {}

function parseTypeArgs(
  fieldName: string,
  propSchema: Record<string, unknown>,
): { precision: number; scale: number } {
  const typeArgs = isPlainObject(propSchema.typeArgs)
    ? propSchema.typeArgs
    : {};
  const precision = typeArgs.precision;
  const scale = typeArgs.scale;
  if (
    typeof precision !== 'number' ||
    !Number.isInteger(precision) ||
    precision < 1
  ) {
    throw new FieldParseError(
      `Field "${fieldName}" is "numeric" but "typeArgs.precision" must be an integer >= 1 (the total digit count).`,
    );
  }
  if (
    typeof scale !== 'number' ||
    !Number.isInteger(scale) ||
    scale < 0 ||
    scale > precision
  ) {
    throw new FieldParseError(
      `Field "${fieldName}" is "numeric" but "typeArgs.scale" must be an integer with 0 <= scale <= precision (the decimal digit count).`,
    );
  }
  return { precision, scale };
}

/**
 * Validates a "default" value against the Postgres column constraints its
 * type will provision (VARCHAR(255), int4 range, NUMERIC(precision, scale),
 * a real calendar date) — the same check `SchemaDefinitionService` runs
 * server-side, so a value accepted here is guaranteed to also be accepted
 * server-side. Fails fast, at parse time, with a message naming the field,
 * instead of a later 400.
 *
 * A "date" default may be written in any AS/400-sourced format
 * `parseFlexibleDateOnly` accepts (YYYY-MM-DD, YYYYMMDD, or YYYY/MM/DD) —
 * this lets a fixture carry the value exactly as it appears in the source
 * file — but is always normalized to canonical `YYYY-MM-DD` here, so the
 * imported field's default is unambiguous regardless of the source format.
 */
function readDefault(
  fieldName: string,
  fieldType: SqlSchemaFieldType,
  numericArgs: SqlNumericArgs | null,
  propSchema: Record<string, unknown>,
): unknown {
  if (!('default' in propSchema)) return undefined;
  const value = propSchema.default;
  // A literal `default: null` means "no default" — same as omitting the key
  // — since every field here is already nullable. Skip validation for it.
  if (value === null) return undefined;
  const error = sqlDefaultValueConstraintError(fieldType, value, numericArgs);
  if (error) {
    throw new FieldParseError(
      `Field "${fieldName}" has a "default" that ${error}.`,
    );
  }
  if (fieldType === 'date' && typeof value === 'string') {
    return parseFlexibleDateOnly(value);
  }
  return value;
}

/**
 * Reads an optional `"indexes": ["btree", ...]` list — the index kinds to
 * provision on the column. Indexes are opt-in: omitted/null and an empty array
 * both mean no indexes. Each kind must be allowed for the field's type.
 */
function readIndexes(
  fieldName: string,
  fieldType: SqlSchemaFieldType,
  propSchema: Record<string, unknown>,
): SchemaFieldIndexType[] | undefined {
  if (!('indexes' in propSchema) || propSchema.indexes == null) {
    return undefined;
  }
  const value = propSchema.indexes;
  const allowed = allowedIndexTypesForFieldType(fieldType);
  if (
    !Array.isArray(value) ||
    !value.every(
      (kind): kind is SchemaFieldIndexType =>
        typeof kind === 'string' &&
        (allowed as readonly string[]).includes(kind),
    )
  ) {
    throw new FieldParseError(
      `Field "${fieldName}" has an "indexes" that must be an array of index types valid for "${fieldType}" (${allowed.join(', ')}).`,
    );
  }
  if (new Set(value).size !== value.length) {
    throw new FieldParseError(
      `Field "${fieldName}" has an "indexes" with duplicate entries.`,
    );
  }
  return value;
}

function readKeyIndex(
  fieldName: string,
  propSchema: Record<string, unknown>,
): number | undefined {
  if (!('keyIndex' in propSchema) || propSchema.keyIndex == null) {
    return undefined;
  }
  const value = propSchema.keyIndex;
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
    throw new FieldParseError(
      `Field "${fieldName}" has a "keyIndex" that must be a non-negative integer.`,
    );
  }
  return value;
}

// Mirrors the server-side column-name gate in `SchemaDefinitionService`
// (`assertValidExtensionField`) so a bad key fails at paste time, naming the
// key, instead of as a later 400.
const COLUMN_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]*$/;
const MAX_COLUMN_NAME_LENGTH = 63;

function importedColumnLabel(fieldName: string, title: unknown): string {
  if (typeof title === 'string' && title.trim()) return title.trim();
  return toDisplayLabel(fieldName);
}

function parseProperty(
  rawKey: string,
  propSchema: Record<string, unknown>,
): { node: ImportFieldNode; keyIndex: number | undefined } {
  // Used verbatim: a "sql" schema's field name becomes the ingest table's
  // column name, so the key must stay byte-identical to the source column
  // (`item_no`, not `itemNo`) for parse configs written against the raw
  // source — and for the `rexel_*` constraints these schemas mirror — to
  // line up. See this directory's README.
  const fieldName = rawKey;
  if (!COLUMN_NAME_PATTERN.test(fieldName)) {
    throw new FieldParseError(
      `Field "${fieldName}" is not a valid column name. Use a letter followed by letters, digits, or underscores.`,
    );
  }
  if (fieldName.length > MAX_COLUMN_NAME_LENGTH) {
    throw new FieldParseError(
      `Field "${fieldName}" is ${fieldName.length} characters; a column name must be ${MAX_COLUMN_NAME_LENGTH} or fewer.`,
    );
  }
  const label = importedColumnLabel(fieldName, propSchema.title);
  const description = describe(propSchema);
  const isRequired = propSchema.required === true;

  const rawType = propSchema.type;
  if (typeof rawType !== 'string' || !SQL_SCHEMA_FIELD_TYPE_SET.has(rawType)) {
    throw new FieldParseError(
      `Field "${fieldName}" has type "${String(rawType)}", which "sql" schemas don't support. Supported types are: ${SQL_SCHEMA_FIELD_TYPES.join(', ')}.`,
    );
  }
  const fieldType = rawType as SqlSchemaFieldType;

  let node: ImportFieldNode;
  let numericArgs: SqlNumericArgs | null = null;
  if (fieldType === 'numeric') {
    const { precision, scale } = parseTypeArgs(fieldName, propSchema);
    numericArgs = { precision, scale };
    node = {
      fieldName,
      fieldType: 'numeric',
      label,
      description,
      isRequired,
      validationRules: { precision, scale },
    };
  } else {
    node = { fieldName, fieldType, label, description, isRequired };
  }

  const defaultValue = readDefault(
    fieldName,
    fieldType,
    numericArgs,
    propSchema,
  );
  if (defaultValue !== undefined) {
    node.defaultValue = defaultValue;
  }

  const indexes = readIndexes(fieldName, fieldType, propSchema);
  if (indexes !== undefined) {
    node.indexes = indexes;
  }

  const keyIndex = readKeyIndex(fieldName, propSchema);

  return { node, keyIndex };
}

/**
 * Validates that the collected `keyIndex` values form a contiguous 0-based
 * sequence (no gaps, no duplicates) and returns the field names ordered by
 * index — the composite key, in column order.
 */
function resolveKeyedBy(
  entries: readonly { fieldName: string; keyIndex: number }[],
): string[] {
  if (entries.length === 0) return [];

  const sorted = [...entries].sort((a, b) => a.keyIndex - b.keyIndex);
  const seen = new Set<number>();
  for (const { fieldName, keyIndex } of sorted) {
    if (seen.has(keyIndex)) {
      throw new FieldParseError(
        `Duplicate "keyIndex": ${keyIndex} (field "${fieldName}"). Each key field must have a unique index.`,
      );
    }
    seen.add(keyIndex);
  }
  for (let i = 0; i < entries.length; i++) {
    if (!seen.has(i)) {
      throw new FieldParseError(
        `"keyIndex" values must be sequential starting at 0 with no gaps — missing index ${i} (found ${entries.length} key field(s)).`,
      );
    }
  }
  return sorted.map((e) => e.fieldName);
}

function parseObjectProperties(schemaNode: Record<string, unknown>): {
  fields: ImportFieldNode[];
  keyedBy: string[];
} {
  if (!isPlainObject(schemaNode.properties)) {
    return { fields: [], keyedBy: [] };
  }

  const fields: ImportFieldNode[] = [];
  const keyEntries: { fieldName: string; keyIndex: number }[] = [];

  for (const [rawKey, propValue] of Object.entries(schemaNode.properties)) {
    if (!isPlainObject(propValue)) continue;
    const { node, keyIndex } = parseProperty(rawKey, propValue);
    fields.push(node);
    if (keyIndex !== undefined) {
      keyEntries.push({ fieldName: node.fieldName, keyIndex });
    }
  }

  return { fields, keyedBy: resolveKeyedBy(keyEntries) };
}

/**
 * Locate the schema root node. Supports both:
 *   { schema: { type: "object", properties: {...} } }
 *   { type: "object", properties: {...} }
 */
function findRootSchema(
  parsed: Record<string, unknown>,
): Record<string, unknown> | null {
  if (isPlainObject(parsed.schema) && isPlainObject(parsed.schema.properties)) {
    return parsed.schema;
  }
  if (isPlainObject(parsed.properties)) {
    return parsed;
  }
  return null;
}

/**
 * Parse a pasted "sql" schema JSON into a tree of import nodes plus the
 * composite key (if any). This is a compact, purpose-built format — not
 * literal JSON Schema — since "sql" schemas exist to be easy to read/write by
 * hand, not to validate against generic JSON Schema tooling. Every field is a
 * flat, top-level scalar column:
 *
 *   {
 *     "schema": {
 *       "type": "object",
 *       "properties": {
 *         "item_no":   { "type": "string", "required": true, "keyIndex": 0 },
 *         "qty":       { "type": "integer" },
 *         "unit_cost": { "type": "numeric", "typeArgs": { "precision": 13, "scale": 4 } },
 *         "as_of":     { "type": "date", "default": "2026-01-01" }
 *       }
 *     }
 *   }
 *
 * Property keys become field names — and therefore ingest column names —
 * **verbatim**, unlike `extendAISchemaParser`, which camelCases them. A "sql"
 * schema describes real Postgres columns, so `item_no` must stay `item_no`.
 */
export function parseSqlSchema(rawJson: string): ParseResult {
  const input = parseJsonObjectInput(rawJson, 'Paste a JSON schema to import.');
  if (!input.ok) {
    return input;
  }

  const rootSchema = findRootSchema(input.value);
  if (!rootSchema) {
    return {
      ok: false,
      error:
        'Could not find schema.properties. Expected shape: { schema: { type: "object", properties: {...} } }.',
    };
  }

  let parsedProperties: { fields: ImportFieldNode[]; keyedBy: string[] };
  try {
    parsedProperties = parseObjectProperties(rootSchema);
  } catch (err) {
    if (err instanceof FieldParseError) {
      return { ok: false, error: err.message };
    }
    throw err;
  }

  if (parsedProperties.fields.length === 0) {
    return { ok: false, error: 'Schema has no fields under `properties`.' };
  }

  return {
    ok: true,
    fields: parsedProperties.fields,
    keyedBy: parsedProperties.keyedBy,
  };
}

type ExportableSqlSchemaField = {
  fieldName: string;
  fieldType: string;
  description?: string | null;
  isRequired: boolean;
  sortOrder?: number;
  defaultValue?: unknown;
  validationRules?: { precision?: number; scale?: number } | null;
  indexes?: SchemaFieldIndexType[] | null;
};

type JsonSchemaNode = {
  type: string;
  typeArgs?: { precision: number; scale: number };
  required?: boolean;
  default?: unknown;
  keyIndex?: number;
  indexes?: SchemaFieldIndexType[];
  description?: string;
};

export type SqlSchemaConfig = {
  schema: {
    type: 'object';
    properties: Record<string, JsonSchemaNode>;
  };
};

function orderedFields(
  fields: readonly ExportableSqlSchemaField[],
): ExportableSqlSchemaField[] {
  return fields
    .map((field, index) => ({ field, index }))
    .sort((a, b) => {
      const sortDelta =
        (a.field.sortOrder ?? a.index) - (b.field.sortOrder ?? b.index);
      return sortDelta === 0 ? a.index - b.index : sortDelta;
    })
    .map(({ field }) => field);
}

function buildFieldSchema(
  field: ExportableSqlSchemaField,
  keyIndex: number | undefined,
): JsonSchemaNode {
  if (!SQL_SCHEMA_FIELD_TYPE_SET.has(field.fieldType)) {
    throw new UnsupportedSqlSchemaFieldTypeError(
      field.fieldName,
      field.fieldType,
    );
  }
  const fieldType = field.fieldType as SqlSchemaFieldType;

  const node: JsonSchemaNode = { type: fieldType };

  if (fieldType === 'numeric') {
    const precision = field.validationRules?.precision;
    const scale = field.validationRules?.scale;
    if (typeof precision !== 'number' || typeof scale !== 'number') {
      throw new UnsupportedSqlSchemaFieldTypeError(
        field.fieldName,
        'numeric (missing precision/scale)',
      );
    }
    node.typeArgs = { precision, scale };
  }

  if (field.isRequired) {
    node.required = true;
  }
  if (field.defaultValue !== undefined && field.defaultValue !== null) {
    node.default = field.defaultValue;
  }
  if (keyIndex !== undefined) {
    node.keyIndex = keyIndex;
  }
  if (field.indexes != null) {
    node.indexes = field.indexes;
  }
  const trimmedDescription = field.description?.trim();
  if (trimmedDescription) {
    node.description = trimmedDescription;
  }

  return node;
}

function buildProperties(
  fields: readonly ExportableSqlSchemaField[],
  keyedBy: readonly string[],
): Record<string, JsonSchemaNode> {
  const keyIndexByFieldName = new Map(
    keyedBy.map((fieldName, index) => [fieldName, index]),
  );
  // Null-prototype: assigning a field literally named `__proto__` to a normal
  // object would set the prototype instead of creating an own property, and
  // JSON.stringify would then silently drop that column from the export.
  const properties: Record<string, JsonSchemaNode> = Object.create(null);
  for (const field of orderedFields(fields)) {
    // Verbatim, mirroring the import side — the field name IS the column name.
    properties[field.fieldName] = buildFieldSchema(
      field,
      keyIndexByFieldName.get(field.fieldName),
    );
  }
  return properties;
}

/**
 * `keyedBy` is the composite key in column order (field names) — mirrors
 * `SchemaDefinition.keyedBy`. Pass it to round-trip a schema's key back out
 * as each key field's `keyIndex`.
 */
export function buildSqlSchemaConfig(
  fields: readonly ExportableSqlSchemaField[],
  keyedBy: readonly string[] = [],
): SqlSchemaConfig {
  return {
    schema: {
      type: 'object',
      properties: buildProperties(fields, keyedBy),
    },
  };
}

export function stringifySqlSchemaConfig(
  fields: readonly ExportableSqlSchemaField[],
  keyedBy: readonly string[] = [],
): string {
  return JSON.stringify(buildSqlSchemaConfig(fields, keyedBy), null, 2);
}
